import React, { useEffect, useRef, useState } from 'react';

import { useMetrics } from '../../../hooks/useMetrics';
import { AUTH_EVENTS } from '../../../utils/authFunnel';
import { configureStore } from '../../../utils/store';
import {
  CenterWrap,
  CodeBoxes,
  LinkBtn,
  PrimaryBtn,
  StepBack,
  Sticker,
} from './authStyles';
import { AuthStrings } from './useAuthStrings';

type Props = {
  t: AuthStrings;
  onBack: () => void;
};

/**
 * Student "Enter join code" flow. Students don't sign in — the code routes them
 * to the live-lesson join URL, mirroring the PinInput in LandingPageHeader.
 */
const JoinCodeView: React.FC<Props> = ({ t, onBack }) => {
  const metrics = useMetrics(true);
  const [digits, setDigits] = useState(['', '', '', '', '', '']);
  const refs = useRef<(HTMLInputElement | null)[]>([]);
  const startedRef = useRef(false);

  // Funnel: the user opened the join-code entry (i.e. is trying to enter a PIN).
  // Ref-guarded so it fires once per mount (not twice under React strict mode).
  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    metrics.logEvent(AUTH_EVENTS.joinStarted);
  }, [metrics]);

  const set = (idx: number, val: string) => {
    const v = val.replace(/\D/g, '').slice(-1);
    setDigits(d => {
      const n = [...d];
      n[idx] = v;
      return n;
    });
    if (v && idx < 5) refs.current[idx + 1]?.focus();
  };

  const onKey = (idx: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !digits[idx] && idx > 0) refs.current[idx - 1]?.focus();
  };

  const onPaste = (e: React.ClipboardEvent) => {
    const txt = (e.clipboardData.getData('text') || '').replace(/\D/g, '').slice(0, 6);
    if (!txt) return;
    e.preventDefault();
    const n = ['', '', '', '', '', ''];
    txt.split('').forEach((c, k) => (n[k] = c));
    setDigits(n);
    refs.current[Math.min(txt.length, 5)]?.focus();
  };

  const full = digits.every(d => d !== '');

  const submit = () => {
    if (!full) return;
    const pin = digits.join('');
    // Funnel: a complete PIN was submitted and the student is being sent to the
    // live-lesson join URL.
    metrics.logEvent(AUTH_EVENTS.joinRedirected);
    window.open(`https://${configureStore().config.game.url}/join/${pin}`);
  };

  return (
    <CenterWrap>
      <Sticker src="/auth/stickers/star-struck.svg" alt="" />
      <h2>{t.joinTitle}</h2>
      <p>{t.joinSub}</p>
      <CodeBoxes onPaste={onPaste}>
        {digits.map((d, idx) => (
          <input
            key={idx}
            ref={el => (refs.current[idx] = el)}
            value={d}
            inputMode="numeric"
            maxLength={1}
            className={d ? 'filled' : ''}
            onChange={e => set(idx, e.target.value)}
            onKeyDown={e => onKey(idx, e)}
            aria-label={`${t.joinDigitLabel} ${idx + 1}`}
          />
        ))}
      </CodeBoxes>
      <div
        style={{
          fontSize: 13,
          fontWeight: 700,
          color: 'rgba(40,47,51,.38)',
          marginBottom: 22,
        }}
      >
        {t.joinHint}
      </div>
      <PrimaryBtn disabled={!full} onClick={submit}>
        {t.joinBtn}
      </PrimaryBtn>
      <StepBack>
        <LinkBtn onClick={onBack}>← {t.back}</LinkBtn>
      </StepBack>
    </CenterWrap>
  );
};

export default JoinCodeView;
